'use client';
import Seo from '@repo/ui/src/components/Seo';
import { prependSlash } from '@repo/ui/src/components/Anchor/helpers';
import { SeoProps } from '@repo/ui/src/types/seo';
import { isSSR } from '@repo/ui/src/utils/isSSR';
import React, { useState } from 'react';

import MobileNavigationToggle from '@components/MobileNavigationToggle';
import { Anchor } from '@repo/ui/src/components/Anchor';
import { useLogout } from '@hooks/auth';
import { classNames } from '@repo/ui/src/utils/classNames';
import { useCustomer } from '@hooks/customer';
import { navigationClicked } from '@repo/ui/src/services/analytics';
import { LinkProps } from '@repo/ui/src/types/link';

export interface MenuLink {
  isLogout: boolean;
  link: LinkProps;
}

export interface AccountLayoutType {
  greeting?: string;
  links?: Array<MenuLink>;
}

interface Props {
  children: React.ReactNode;
  layout: AccountLayoutType;
  header?: string;
  description?: string;
  seo: SeoProps;
}

const AccountLayout = ({ children, layout, header, description, seo }: Props) => {
  const { trigger } = useLogout();
  const { data: user } = useCustomer();
  const [isOpen, toggle] = useState(false);
  const [dropdownLabel, setDropdownLabel] = useState(
    layout?.links?.[0]?.link?.displayName ?? 'Account Dashboard',
  );

  const selected = (link: MenuLink) =>
    !isSSR && location.pathname === prependSlash(link?.link?.link);

  return (
    <div className="md:tw-px-14 lg:tw-px-20">
      {seo && <Seo {...seo} contentRows={[]} />}
      <div className="tra-layout-aside md:tw-grid-cols-aside sm:tw-gap-x-14 lg:tw-gap-x-28 tw-max-w-body">
        <aside className="tw-gap-y-4 tw-bg-warm-200 after:tw-bg-warm-200">
          <MobileNavigationToggle
            className="tw-my-6 tw-mx-10 tw-bg-white tw-py-3 tw-px-4 tw-border tw-border-neutral-500 md:tw-hidden"
            toggle={toggle}
            isOpen={isOpen}
            label={dropdownLabel}
            hideOnDesktop
            account
          />
          <div
            data-testid="account-layout-nav-wrapper"
            className={classNames(
              'tw-absolute tw-border tw-border-warm-400 tw-width-full tw-top-6 tw-left-10 tw-right-10 tw-bg-neutral-100 md:tw-py-10 md:tw-block md:tw-sticky md:tw-my-10 md:tw-mr-14 lg:tw-mr-28 ',
              isOpen ? 'tw-block' : 'tw-hidden',
            )}
          >
            <h4 className="tra-text-web-heading-4 tw-pb-8 tw-px-8 tw-hidden md:tw-block">
              {layout?.greeting ?? 'Welcome,'}&nbsp;
              {user?.firstName}
            </h4>
            <ul className="tw-list-none tw-pl-0">
              {layout?.links?.map((link, i) => (
                <li key={i} aria-label="menu" aria-current={selected(link)}>
                  <Anchor
                    className={classNames(
                      'tw-block tw-outline-offset-[-2px] tw-text-neutral-400 tw-outline-brand-100 tw-outline-2',
                      'focus-visible:tw-outline',
                      'hover:tw-bg-brand-50 hover:tw-underline hover:tw-text-neutral-600',
                      selected(link) && 'tw-bg-brand-50 tw-text-neutral-600',
                    )}
                    onClick={() => {
                      if (link?.isLogout) {
                        trigger();
                      }
                      navigationClicked({
                        nav_type: 'Account Sidebar',
                        link_url: link?.link?.link,
                        link_location: 'My Account Navigation',
                        clicked_text: link?.link?.displayName ?? '',
                        primary_header: header ?? '',
                      });
                      if (link?.link?.displayName) {
                        setDropdownLabel(link?.link?.displayName);
                      }
                      toggle(!isOpen);
                    }}
                    link={link?.link?.link}
                    label={link?.link?.displayName}
                  >
                    <p className="tra-text-web-body-xl tw-px-4 tw-py-3 md:tw-px-8">
                      {link?.link?.displayName}
                    </p>
                  </Anchor>
                </li>
              ))}
            </ul>
          </div>
        </aside>

        <article className="tw-px-6 tw-py-10 md:tw-px-0">
          <header className="tw-mb-15 md:tw-mb-20">
            <h2 className="tra-text-web-heading-2 tw-mb-5" tabIndex={0}>
              {header}
            </h2>
            <hr className="tw-border-2 tw-w-12 tw-border-brand-300 tw-my-4" />
            {description && (
              <p className="tra-text-web-body-xl tw-max-w-[370px]" tabIndex={0}>
                {description}
              </p>
            )}
          </header>
          {children}
        </article>
      </div>
    </div>
  );
};

export default AccountLayout;
